import React from 'react'
import { useParams, useLocation } from 'react-router-dom'
import SEO from './SEO'
import {
  getCurrentDomain,
  getSEOForRoute,
  getBaseUrl,
} from '../utils/seoConfig'
import { generateWebsiteSchema } from '../services/schemaGenerators'
import { getCountryBySlug } from '../services/newsConfig'

/**
 * StaticPageWrapper with Domain-Aware SEO
 * Usage:
 * <StaticPageWrapper useDomainSEO>
 *   <Homepage />
 * </StaticPageWrapper>
 *
 * OR with manual override:
 * <StaticPageWrapper title="Custom Title" description="Custom desc">
 *   <Component />
 * </StaticPageWrapper>
 */
export const StaticPageWrapper = ({
  children,
  title,
  description,
  canonical,
  keywords,
  ogImage = '/web-app-manifest-512x512.png',
  schemaMarkup = null,
  useDomainSEO = true, // Default to true for static pages
}) => {
  const location = useLocation()
  const domain = getCurrentDomain()
  const baseUrl = getBaseUrl()

  // Load domain-specific SEO if enabled and no manual values provided
  let finalTitle = title
  let finalDescription = description
  let finalKeywords = keywords
  let finalCanonical = canonical

  if (useDomainSEO && !title) {
    const routeSEO = getSEOForRoute(location.pathname, domain)
    finalTitle = routeSEO.title
    finalDescription = routeSEO.description
    finalKeywords = routeSEO.keywords
    finalCanonical = routeSEO.canonical
  }

  // Ensure canonical URL is absolute and normalized
  const normalizedCanonical = finalCanonical
    ? finalCanonical.startsWith('http')
      ? finalCanonical
      : `${baseUrl}${finalCanonical}`
    : `${baseUrl}${location.pathname}`

  return (
    <>
      <SEO
        title={finalTitle}
        description={finalDescription}
        canonical={normalizedCanonical}
        keywords={finalKeywords}
        schemaMarkup={schemaMarkup}
        ogImage={ogImage}
        useDomainSEO={useDomainSEO && !title}
      />
      {children}
    </>
  )
}

/**
 * LawPageWrapper with Domain-Aware SEO
 * Generates dynamic SEO for law structure pages
 */
export const LawPageWrapper = ({ children }) => {
  const { country, lawShortName, path } = useParams()
  const domain = getCurrentDomain()
  const baseUrl = getBaseUrl()

  const lawDisplayNamesObj = {
    'common-customs-agreement': 'GCC Common Customs Agreement',
    'common-vat-agreement': 'GCC Common VAT Agreement',
    'common-excise-agreement': 'GCC Common Excise Agreement',
    'cit-fdl-47-of-2022': 'UAE CIT (FDL No 47 of 2022, as amended)',
    'dmtt-fdl-60-of-2023': 'UAE DMTT (FDL No 60 of 2023)',
    'indirecttax-law-vat-uaevat-8-of-2017-as-amended':
      'UAE VAT (FDL No 8 of 2017, as amended)',
    'tp-fdl-28-of-2022-as-amended':
      'UAE Tax Procedures (FDL No 28 of 2022, as amended)',
    'indirect-excise-7-of-2017-as-amended':
      'UAE Excise Tax (FDL No 7 of 2017, as amended)',
    'fatca-of-2024': 'UAE FATCA (Foreign Account Tax Compliance Act) 2024',
    'crs-federal-decree-48-of-2018':
      'UAE CRS (Common Reporting Standard, Federal Decree No 48 of 2018)',
    'fatca-of-2014-iga-m1':
      'Qatar FATCA (Foreign Account Tax Compliance Act) 2014',
    'fatca-of-2014-direct':
      'Oman FATCA (Foreign Account Tax Compliance Act) 2014',
    fatca: 'Foreign Account Tax Compliance Act', // since there is no standard unique code for this law, and income tax can be present in other countries as well so removing UAE
    'draft-business-profit-tax-law-of-2024':
      'Kuwait Draft Business Profit Tax Law 2024',
    'kit-law-no-2-of-2008-amending-decree-no-3-of-1955':
      'Kuwait Income Tax Law - Decree No. 3 of 1955',
    'qdmtt-decree-law-157-of-2024': 'Kuwait QDMTT Law - Decree No. 157 of 2024',
    'income-tax-law': 'Income Tax Law', // since there is no standard unique code for this law, and income tax can be present in other countries as well so removing KSA
    'income-tax-law-of-2004': 'KSA Income Tax Law 2004',
    'vat-law-m-113-of-2017': 'KSA VAT Law 2017',
    'fatca-of-2017': 'KSA FATCA (Foreign Account Tax Compliance Act) 2017',
    'income-tax-law-24-of-2018': 'Qatar Income Tax Law 24 of 2018',
    'dmtt-fdl-22-of-2024': 'Qatar DMTT (FDL No 22 of 2024)',
    'income-tax-law-royal-decree-28-of-2009':
      'Oman Income Tax Law (Royal Decree No 28 of 2009, as amended)',
    'vat-law': 'VAT Law', // since there is no standard unique code for this law, and income tax can be present in other countries as well so removing KSA
    'vat-law-royal-decree-121-of-2020': 'Oman VAT Law 2020',
    'excise-law': 'Excise Law', // since there is no standard unique code for this law, and income tax can be present in other countries as well so removing KSA
    'excise-law-of-2019': 'KSA Excise Law 2019',
    'excise-tax-law-royal-decree-23-of-2019':
      'Oman Excise Tax Law (Royal Decree No 23 of 2019)',
    'excise-law-25-of-2018': 'Qatar Excise Law 2018',
    'tax-procedures-law': 'Tax Procedures Law',
    'dmtt-law-11-of-2024': 'Bahrain DMTT Law (Decree Law No. 11 of 2024)',
    'vat-law-48-of-2018': 'Bahrain VAT Law 48 of 2018',
    'cbcr-law': 'Country-by-Country Reporting Law', // since there is no standard unique code for this law, and income tax can be present in other countries as well so removing KSA
    'customs-law': 'Customs Law', // since there is no standard unique code for this law, and customs can be present in other countries as well so removing KSA
    'zakat-law-of-1951': 'KSA Zakat Collection Law 1951',
  }

  const generateLawSEO = () => {
    const countryName = country?.toUpperCase()
    const lawDisplayName =
      lawDisplayNamesObj[lawShortName] || `${country} Tax Law`

    // Check if there's custom SEO for this exact route
    const currentPath = `/laws/${country.toLowerCase()}/${lawShortName}`
    const routeSEO = getSEOForRoute(currentPath, domain)
    const homepageSEO = getSEOForRoute('/', domain)

    let title, description, keywords, canonical

    // If custom SEO exists for this law page, use it
    // Check if the returned SEO is different from homepage (means we found custom SEO)
    if (routeSEO && routeSEO.title && routeSEO.title !== homepageSEO.title) {
      title = routeSEO.title
      description = routeSEO.description
      keywords = routeSEO.keywords
      canonical = routeSEO.canonical
    } else {
      // Generate dynamic SEO
      if (path) {
        // Specific section page
        title = `${path} | ${lawDisplayName} | ${countryName}`
        description = `Access ${path} of ${lawDisplayName} in ${countryName}. Complete legal text and analysis.`
        canonical =
          `${baseUrl}/laws/${country}/${lawShortName}/${path}`.replace(
            /\/+$/,
            '',
          )
      } else {
        // Main law page
        title = `${lawDisplayName} | ${countryName}`
        description = `Complete ${lawDisplayName} for ${countryName}. Browse articles, definitions, and legal provisions.`
        canonical = `${baseUrl}/laws/${country}/${lawShortName}`.replace(
          /\/+$/,
          '',
        )
      }
      keywords = `${country} tax law, ${lawShortName}, ${lawDisplayName}`
    }

    return { title, description, canonical, keywords }
  }

  const seoData = generateLawSEO()

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        canonical={seoData.canonical}
        keywords={seoData.keywords}
      />
      {children}
    </>
  )
}

/**
 * TaxTreatyPageWrapper with Domain-Aware SEO
 */
export const TaxTreatyPageWrapper = ({ children }) => {
  const { country } = useParams()
  const domain = getCurrentDomain()
  const baseUrl = getBaseUrl()

  const generateTaxTreatySEO = () => {
    const countryName = country?.toUpperCase() || 'GCC'

    // Check for custom SEO
    const currentPath = `/search/${country}/Tax-Treaties`
    const routeSEO = getSEOForRoute(currentPath, domain)
    const homepageSEO = getSEOForRoute('/', domain)
    // If custom SEO exists, use it
    // Check if the returned SEO is different from homepage (means we found custom SEO)
    if (routeSEO && routeSEO.title && routeSEO.title !== homepageSEO.title) {
      return {
        title: routeSEO.title,
        description: routeSEO.description,
        keywords: routeSEO.keywords,
        canonical: routeSEO.canonical,
      }
    }

    // Generate dynamic SEO
    const title = `${countryName} Tax Treaties | Double Taxation Agreements`
    const description = `Access comprehensive ${countryName} tax treaties and double taxation agreements. Browse bilateral tax agreements, withholding tax rates, and treaty benefits for ${countryName}.`
    const canonical = `${baseUrl}/search/${country}/Tax-Treaties`.replace(
      /\/+$/,
      '',
    )
    const keywords = `${country} tax treaties, double taxation agreement, bilateral tax treaty, withholding tax rates, treaty benefits, ${countryName} DTA, international tax`

    return { title, description, canonical, keywords }
  }

  const seoData = generateTaxTreatySEO()
  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        canonical={seoData.canonical}
        keywords={seoData.keywords}
      />
      {children}
    </>
  )
}

/**
 * CountryNewsPageWrapper with Domain-Aware SEO
 */
export const CountryNewsPageWrapper = ({ children }) => {
  const { country: countrySlug } = useParams()
  const domain = getCurrentDomain()
  const baseUrl = getBaseUrl()
  
  // Check for custom SEO
  const pathname = `/news/${countrySlug || ''}`
  const routeSEO = getSEOForRoute(pathname, domain)
  const homepageSEO = getSEOForRoute('/', domain)

  // If seoData.js has a specific entry for this country, use it
  if (routeSEO && routeSEO.title && routeSEO.title !== homepageSEO.title) {
    return (
      <>
        <SEO
          title={routeSEO.title}
          description={routeSEO.description}
          canonical={routeSEO.canonical}
          keywords={routeSEO.keywords}
        />
        {children}
      </>
    )
  }

  // Generate dynamic SEO
  const country = getCountryBySlug(countrySlug)
  const title = country
    ? `${country.fullName} Tax News | GCC Tax Laws`
    : 'Country Tax News | GCC Tax Laws'
  const description = country
    ? `Latest ${country.fullName} tax news, regulatory updates, and announcements. Stay informed on ${country.name} tax law changes from official sources and leading media.`
    : 'Latest GCC country tax news and regulatory updates.'
  const canonical = `${baseUrl}${pathname}`.replace(/\/+$/, '')
  const keywords = country
    ? `${country.fullName} tax news, ${country.name} tax updates, ${country.name} regulatory news, GCC tax news, ${country.fullName} tax law`
    : 'GCC tax news'

  return (
    <>
      <SEO
        title={title}
        description={description}
        canonical={canonical}
        keywords={keywords}
      />
      {children}
    </>
  )
}

export default {
  StaticPageWrapper,
  LawPageWrapper,
  TaxTreatyPageWrapper,
  CountryNewsPageWrapper,
}
